import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, MapPin, CalendarCheck } from "lucide-react";

/* Página aberta pelo QR Code à entrada: marca presença com sessão iniciada ou com o email. */
export default function PublicCheckin() {
  const { token } = useParams();
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/public/checkin/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(formatApiError(e)));
  }, [token]);

  const mark = async (withSession) => {
    setBusy(true); setError(null);
    try {
      const r = withSession
        ? await api.post(`/public/checkin/${token}/me`)
        : await api.post(`/public/checkin/${token}`, { email });
      setDone(r.data);
    } catch (e) { setError(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
      <div className="kw-card max-w-md w-full p-8 kw-fade-up" data-testid="checkin-page">
        <div className="kw-overline text-[#E50000] mb-2">KW Lead Growth League · Presença</div>
        {info && (
          <>
            <h1 className="font-display text-3xl font-black uppercase tracking-tight" data-testid="checkin-title">{info.title}</h1>
            <div className="text-sm text-[#A3A3A3] mt-2 flex items-center gap-1.5"><CalendarCheck className="w-4 h-4" /> {info.when_label}</div>
            <div className="text-xs text-[#737373] mt-1 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {info.location}</div>
          </>
        )}
        {done ? (
          <div className="mt-8 text-center" data-testid="checkin-success">
            <CheckCircle2 className="w-14 h-14 text-[#22C55E] mx-auto" />
            <div className="font-display text-2xl font-black uppercase mt-3">
              {done.already ? "Já estavas marcado(a)" : "Presença registada"}
            </div>
            <p className="text-sm text-[#A3A3A3] mt-1">Bem-vindo(a), {(done.name || "").split(" ")[0]}! Boa {done.kind === "training" ? "formação" : "reunião"}.</p>
            {done.kind === "training"
              ? <p className="text-xs text-[#22C55E] mt-3" data-testid="checkin-points">+8 pontos de formação atribuídos</p>
              : <p className={`text-xs mt-3 ${done.punctual ? "text-[#22C55E]" : "text-[#EAB308]"}`} data-testid="checkin-punctual">
                  {done.punctual ? "Pontual · +5 pontos de pontualidade" : "Chegaste depois da hora — presença registada, sem os +5 da pontualidade"}
                </p>}
          </div>
        ) : info && (
          <div className="mt-8 space-y-4">
            {user && (
              <button onClick={() => mark(true)} disabled={busy} className="kw-btn-primary w-full !py-4"
                data-testid="checkin-me">
                {busy ? "A registar…" : `Marcar presença como ${(user.name || "").split(" ")[0]}`}
              </button>
            )}
            {user === false && (
              <>
                <label className="kw-overline text-[10px] block">O teu email</label>
                <input type="email" className="kw-input" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@kwportugal.pt" data-testid="checkin-email" />
                <button onClick={() => mark(false)} disabled={busy || !email.includes("@")} className="kw-btn-primary w-full !py-4"
                  data-testid="checkin-submit">
                  {busy ? "A registar…" : "Estou presente"}
                </button>
                <a href={`/login?next=/presenca/${token}`} className="block text-center text-xs text-[#737373] hover:text-white"
                  data-testid="checkin-login">Ou entra na app</a>
              </>
            )}
          </div>
        )}
        {error && <p className="text-sm text-[#E50000] mt-4" data-testid="checkin-error">{error}</p>}
      </div>
    </div>
  );
}
